/*
 * Paros — lazy sections & lazy tabs
 *
 * Two kinds of placeholder are supported:
 *   1. [data-lazy-section][data-lazy-url]  -> the section's products are NOT in the page HTML.
 *      They are fetched from Shopify's Section Rendering API shortly before the section
 *      scrolls into view. This keeps the first HTML response small and fast.
 *   2. [data-lazy-section] / [data-lazy-tab-content] holding a <template>  -> content is
 *      already in the page and is simply activated on demand.
 */
(function () {
  'use strict';

  var ROOT_MARGIN = '800px 0px'; // start loading ~800px before the section is visible
  var cache = new Map();         // url -> Promise<string>

  function fetchHTML(url) {
    if (!cache.has(url)) {
      var req = fetch(url, { credentials: 'same-origin' }).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      });
      req.catch(function () { cache.delete(url); });
      cache.set(url, req);
    }
    return cache.get(url);
  }

  // ---- Tabs ---------------------------------------------------------------
  function hydrateTabItem(tabItem) {
    var placeholder = tabItem.querySelector('[data-lazy-tab-content]');
    if (!placeholder) return;
    var template = placeholder.querySelector(':scope > template');
    if (!template) return;
    placeholder.appendChild(template.content.cloneNode(true));
    placeholder.removeAttribute('style');
    template.remove();
    announce(placeholder);
  }

  var tabObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.target.hasAttribute('tab-selected')) hydrateTabItem(m.target);
    });
  });

  function setupTabsWithin(root) {
    var tabItems = Array.prototype.slice.call(root.querySelectorAll('[tab-item]'));
    if (root.matches && root.matches('[tab-item]')) tabItems.unshift(root);
    tabItems.forEach(function (tabItem) {
      if (tabItem.hasAttribute('tab-selected')) hydrateTabItem(tabItem);
      tabObserver.observe(tabItem, { attributes: true, attributeFilter: ['tab-selected'] });
    });

    // The tab bar (<hdt-tab>) looks up its panels when it first connects. For lazy
    // sections the panels did not exist yet at that moment, so (re)connect it now —
    // otherwise clicking a tab does nothing.
    root.querySelectorAll('[id]').forEach(function (panelWrap) {
      if (!panelWrap.querySelector('[tab-item]')) return;
      document.querySelectorAll('hdt-tab[aria-controls="' + panelWrap.id + '"]').forEach(function (tab) {
        if (typeof tab.connectedCallback !== 'function') return; // not upgraded yet: it will find the panels itself
        try {
          if (tab.items === undefined) {
            tab.connectedCallback();
          } else if (!tab.items.length) {
            tab.disconnectedCallback();
            tab.connectedCallback();
          }
        } catch (e) { /* keep the page alive whatever happens */ }
      });
    });
  }

  // ---- Sections -----------------------------------------------------------
  function announce(el) {
    // Same events the theme fires after injecting products (prices, wishlist state, reveal…)
    el.dispatchEvent(new CustomEvent('products:update', { bubbles: true }));
    el.dispatchEvent(new CustomEvent('lazyhtml:added', { bubbles: true }));
  }

  function fill(placeholder, fragment) {
    placeholder.appendChild(fragment);
    placeholder.removeAttribute('style'); // drop the temporary min-height
    placeholder.removeAttribute('data-lazy-url');
    placeholder.setAttribute('data-lazy-loaded', '');
    setupTabsWithin(placeholder);
    announce(placeholder);
  }

  function hydrateSection(placeholder) {
    if (placeholder.hasAttribute('data-lazy-loaded') || placeholder.hasAttribute('data-lazy-loading')) return;

    var template = placeholder.querySelector(':scope > template');
    if (template) {
      var content = template.content.cloneNode(true);
      template.remove();
      fill(placeholder, content);
      return;
    }

    var url = placeholder.getAttribute('data-lazy-url');
    if (!url) return;
    placeholder.setAttribute('data-lazy-loading', '');

    var attempt = function (retries) {
      return fetchHTML(url).then(function (html) {
        // createContextualFragment keeps any <script> inside the section runnable
        var doc = document.createRange().createContextualFragment(html);
        var payload = doc.querySelector('[data-lazy-payload]');
        if (!payload) throw new Error('lazy payload missing');
        var fragment = document.createDocumentFragment();
        while (payload.firstChild) fragment.appendChild(payload.firstChild);
        placeholder.removeAttribute('data-lazy-loading');
        fill(placeholder, fragment);
      }).catch(function (err) {
        if (retries > 0) {
          return new Promise(function (r) { setTimeout(r, 1200); }).then(function () { return attempt(retries - 1); });
        }
        placeholder.removeAttribute('data-lazy-loading');
        placeholder.setAttribute('data-lazy-failed', '');
        placeholder.removeAttribute('style');
        if (window.console) console.warn('[Paros] lazy section failed:', url, err);
      });
    };
    attempt(1);
  }

  var sectionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          hydrateSection(entry.target);
        });
      }, { rootMargin: ROOT_MARGIN, threshold: 0 })
    : null;

  function observeWithin(root) {
    root.querySelectorAll('[data-lazy-section]:not([data-lazy-loaded])').forEach(function (el) {
      if (sectionObserver) sectionObserver.observe(el); else hydrateSection(el);
    });
  }

  function init() {
    observeWithin(document);
    setupTabsWithin(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: a section that gets re-rendered needs its placeholders wired up again
  document.addEventListener('shopify:section:load', function (e) {
    observeWithin(e.target);
    setupTabsWithin(e.target);
  });
})();
